import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export interface OrgInvitation {
  id: string;
  org_id: string;
  email: string;
  role: string;
  invited_by: string;
  token: string;
  status: string;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
  updated_at: string;
  organizations?: { name: string; logo_url: string | null };
}

const inv = () => supabase.from("org_invitations");

export const useMyInvitations = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["my_invitations", user?.email],
    enabled: !!user?.email,
    queryFn: async () => {
      const { data, error } = await inv()
        .select("*")
        .eq("email", user!.email!)
        .in("status", ["pending", "expired"])
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as unknown as OrgInvitation[];
    },
  });
};

export const useOrgInvitations = (orgId?: string) =>
  useQuery({
    queryKey: ["org_invitations", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await inv().select("*").eq("org_id", orgId!).order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as OrgInvitation[];
    },
  });

export const useSendOrgInvitation = () => {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: { org_id: string; email: string; role?: string }) => {
      const { data, error } = await inv()
        .insert({
          org_id: input.org_id,
          email: input.email.toLowerCase().trim(),
          role: input.role || "admin",
          invited_by: user!.id,
        })
        .select()
        .single();
      if (error) throw error;
      return data as OrgInvitation;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org_invitations"] });
      qc.invalidateQueries({ queryKey: ["master_org_invitations"] });
      toast.success("Invitation sent");
    },
    onError: (e: Error) => {
      toast.error(e.message.includes("duplicate") ? "An active invitation already exists for this email" : e.message);
    },
  });
};

export const useAcceptInvitation = () => {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { data: invitation, error: fetchErr } = await inv().select("*").eq("id", invitationId).maybeSingle();
      if (!invitation) throw new Error("Invitation not found");
      if (fetchErr) throw fetchErr;
      const i = invitation as OrgInvitation;
      if (i.status !== "pending") throw new Error("Invitation is no longer valid");
      if (new Date(i.expires_at) < new Date()) throw new Error("Invitation has expired");

      const { error: memberErr } = await supabase.from("organization_users").upsert(
        {
          organization_id: i.org_id,
          user_id: user!.id,
          role_type: i.role === "admin" ? "org_admin" : "org_staff",
          status: "active",
          joined_at: new Date().toISOString(),
        },
        { onConflict: "organization_id,user_id" },
      );
      if (memberErr) throw memberErr;

      await inv().update({ status: "accepted", accepted_at: new Date().toISOString() }).eq("id", invitationId);
      return i;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my_invitations"] });
      toast.success("Invitation accepted! Welcome to your organization.");
      // Force reload to pick up new org
      setTimeout(() => window.location.reload(), 1000);
    },
    onError: (e: Error) => toast.error(e.message),
  });
};

export const useResendInvitation = () => {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (invitation: OrgInvitation) => {
      await inv().update({ status: "expired" }).eq("id", invitation.id);
      const { data, error } = await inv()
        .insert({ org_id: invitation.org_id, email: invitation.email, role: invitation.role, invited_by: user!.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org_invitations"] });
      qc.invalidateQueries({ queryKey: ["my_invitations"] });
      toast.success("Invitation resent");
    },
    onError: (e: Error) => toast.error(e.message),
  });
};

export const useRevokeInvitation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await inv().update({ status: "revoked" }).eq("id", invitationId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org_invitations"] });
      qc.invalidateQueries({ queryKey: ["master_org_invitations"] });
      toast.success("Invitation revoked");
    },
    onError: (e: Error) => toast.error(e.message),
  });
};

export const useAllOrgInvitations = () =>
  useQuery({
    queryKey: ["master_org_invitations"],
    queryFn: async () => {
      // Fetch invitations without join (no FK to organizations)
      const { data: invData, error: invError } = await inv()
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100);
      if (invError) throw invError;

      // Fetch org names for display
      const orgIds = [...new Set((invData || []).map((i) => i.org_id))];
      const orgMap: Record<string, { name: string; logo_url: string | null }> = {};
      if (orgIds.length > 0) {
        const { data: orgs } = await supabase.from("organizations").select("id, name, logo_url").in("id", orgIds);
        (orgs || []).forEach((o) => {
          orgMap[o.id] = { name: o.name, logo_url: o.logo_url };
        });
      }

      return (invData || []).map((i) => ({
        ...i,
        organizations: orgMap[i.org_id] || null,
      })) as unknown as OrgInvitation[];
    },
  });
